/**
 * @fileoverview A gift code opened by somebody signed out, kept in this browser until they
 * sign in or sign up and it is applied to their account (GiftClaim), and the
 * small helpers the gift pages share.
 *
 * Kept in localStorage (GIFT_CODE_STORAGE_KEY), as a founding code is,
 * because signing up is several screens after the link: the sign-in page,
 * then Google, then back. Quiet wherever storage is refused.
 *
 * Where: the redeem page, GiftClaim and GiftCodeBox (tidyGiftCode,
 * keepGiftCode, keptGiftCode, forgetGiftCode); the thanks and redeem pages
 * (giftDay).
 * API: none.
 * Settings: GIFT_CODE_STORAGE_KEY (config/tunables/shared).
 * Watch out: the alphabet leaves out I, L, O, U, V, 0 and 1 (easily misread
 * on paper), so CODE_PATTERN refuses them; a code typed with one is passed
 * on as typed for the backend to call unknown.
 *
 * @module giftCode
 */

import { DateTime } from 'luxon';
import { GIFT_CODE_STORAGE_KEY } from '@/config/tunables/shared';

/** A gift code as the backend stores it: GROW-XXXX-XXXX from its 29 letters and digits. */
const CODE_PATTERN = /^GROW-[A-HJKMNP-TW-Z2-9]{4}-[A-HJKMNP-TW-Z2-9]{4}$/;

/**
 * A code as typed, in the stored form when it can be one: case, spaces and
 * dashes do not matter and GROW is optional. Anything else is returned
 * trimmed and upper-cased, for the backend to answer "unknown".
 *
 * @param raw - As typed or from the link.
 * @returns The code.
 */
export function tidyGiftCode(raw: string): string {
  let text = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (text.startsWith('GROW') && text.length === 12) text = text.slice(4);
  const code = text.length === 8 ? `GROW-${text.slice(0, 4)}-${text.slice(4)}` : raw.trim().toUpperCase();
  return code;
}

/**
 * Keeps a gift code until its gardener is signed in.
 *
 * @param code - As stored.
 */
export function keepGiftCode(code: string): void {
  try {
    if (CODE_PATTERN.test(code)) window.localStorage.setItem(GIFT_CODE_STORAGE_KEY, code);
  } catch { /* private mode: they can type the code into Profile, Subscription */ }
}

/** The gift code waiting to be applied, if one is kept. */
export function keptGiftCode(): string | null {
  try {
    const code = window.localStorage.getItem(GIFT_CODE_STORAGE_KEY);
    return code && CODE_PATTERN.test(code) ? code : null;
  } catch {
    return null;
  }
}

/** Forgets the kept gift code (applied, or refused). */
export function forgetGiftCode(): void {
  try { window.localStorage.removeItem(GIFT_CODE_STORAGE_KEY); } catch { /* nothing to clear */ }
}

/**
 * A day or a moment as a gardener reads it with its year: "December 25, 2027" ("25 de diciembre de 2027").
 *
 * @param iso - YYYY-MM-DD, or an ISO moment.
 * @param language - The page's language.
 * @returns The words, or the text itself when it does not read.
 */
export function giftDay(iso: string | null | undefined, language: string): string {
  if (!iso) return '';
  const at = DateTime.fromISO(iso);
  return at.isValid ? at.setLocale(language).toLocaleString({ month: 'long', day: 'numeric', year: 'numeric' }) : iso;
}
